import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { requestQuoteFromCart } from "@/lib/orders.functions";

export const Route = createFileRoute("/kosarica")({
  head: () => {
    const url = "https://kupish.si/kosarica";
    const title = "Košarica — kupish.si";
    const description = "Pregled vaše košarice na kupish.si. Preverite izbrane izdelke različnih ponudnikov in oddajte naročilo ali povpraševanje.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: Page,
});

function Page() {
  const { items, total, setQty, remove, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const sendRequest = useServerFn(requestQuoteFromCart);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Address form
  const [name, setName] = useState("");
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  // Use sale_price when present
  const displayTotal = items.reduce((s, i) => {
    const sale = (i.product as unknown as { sale_price?: number | null }).sale_price;
    const price = sale != null ? Number(sale) : Number(i.product.price);
    return s + price * i.qty;
  }, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate({ to: "/prijava" });
      return;
    }
    setErr(null);
    setLoading(true);
    try {
      const res = await sendRequest({
        data: {
          items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
          notes: notes.trim() || null,
          shipping_address: {
            name: name.trim(),
            line1: line1.trim(),
            city: city.trim(),
            postal_code: postal.trim(),
            country: "Slovenija",
            phone: phone.trim() || null,
          },
        },
      });
      clear();
      navigate({ to: "/povprasevanje/$groupId", params: { groupId: res.group_id } });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Napaka pri pošiljanju povpraševanja.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <section className="mx-auto max-w-2xl px-6 py-32 text-center">
        <div className="eyebrow">Košarica</div>
        <h1 className="font-serif text-5xl mt-3">Vaša košarica je prazna</h1>
        <Link to="/izdelki" className="mt-8 inline-flex bg-primary text-primary-foreground px-6 py-3 text-sm">
          Razišči izdelke
        </Link>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <div className="eyebrow">Košarica</div>
      <h1 className="font-serif text-5xl mt-3">Pregled povpraševanja</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Vaše naročilo bo poslano prodajalcu kot povpraševanje. Prodajalec vam bo izstavil predračun, ki ga
        plačate po UPN nalogu. Po prejetem plačilu vam izda račun in odpošlje izdelek.
      </p>

      <div className="grid lg:grid-cols-3 gap-12 mt-12">
        <div className="lg:col-span-2 space-y-8">
          {(() => {
            // Group items by seller (vendor) for clearer multi-vendor UX
            const groups = new Map<string, typeof items>();
            for (const it of items) {
              const key = it.product.seller || "Drugo";
              const arr = groups.get(key) ?? [];
              arr.push(it);
              groups.set(key, arr);
            }
            const groupArr = Array.from(groups.entries());
            const multi = groupArr.length > 1;
            return (
              <>
                {multi && (
                  <div className="border-l-2 border-accent bg-accent/5 p-4 text-sm">
                    <div className="font-medium">Vaša košarica vsebuje izdelke {groupArr.length} ponudnikov.</div>
                    <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                      Vsak prodajalec vam bo poslal svoj predračun in svoj paket — to pomeni {groupArr.length} ločenih
                      UPN nakazil in {groupArr.length} pošiljk. Po oddaji povpraševanja boste vse predračune videli
                      na enem skupnem pregledu.
                    </p>
                  </div>
                )}
                {groupArr.map(([seller, sellerItems]) => {
                  const sellerSubtotal = sellerItems.reduce((s, i) => {
                    const sale = (i.product as unknown as { sale_price?: number | null }).sale_price;
                    const u = sale != null ? Number(sale) : Number(i.product.price);
                    return s + u * i.qty;
                  }, 0);
                  return (
                    <div key={seller} className="border border-border">
                      <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                        <div className="text-xs eyebrow">Prodajalec: {seller}</div>
                        <div className="text-xs text-muted-foreground">
                          Vmesni seštevek: <span className="text-accent">€{sellerSubtotal.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="divide-y divide-border">
                        {sellerItems.map(({ product, qty }) => {
                          const sale = (product as unknown as { sale_price?: number | null }).sale_price;
                          const unit = sale != null ? Number(sale) : Number(product.price);
                          return (
                            <div key={product.id} className="flex gap-5 p-4">
                              <Link to="/izdelki/$slug" params={{ slug: product.slug }} className="w-24 h-32 bg-muted overflow-hidden flex-shrink-0">
                                <img src={product.image} alt={product.title} className="h-full w-full object-cover" width={200} height={250} loading="lazy" />
                              </Link>
                              <div className="flex-1 flex flex-col justify-between">
                                <Link to="/izdelki/$slug" params={{ slug: product.slug }} className="font-serif text-xl hover:text-accent">
                                  {product.title}
                                </Link>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center border border-border">
                                    <button type="button" onClick={() => setQty(product.id, qty - 1)} className="px-3 py-1 hover:bg-muted">−</button>
                                    <span className="px-4 text-sm">{qty}</span>
                                    <button type="button" onClick={() => setQty(product.id, qty + 1)} className="px-3 py-1 hover:bg-muted">+</button>
                                  </div>
                                  <div className="flex items-center gap-6">
                                    <div className="font-serif text-lg text-accent">€{(unit * qty).toFixed(2)}</div>
                                    <button type="button" onClick={() => remove(product.id)} className="text-xs text-muted-foreground hover:text-destructive">Odstrani</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>


        <form onSubmit={handleSubmit} className="bg-muted/40 p-8 h-fit space-y-5">
          <h2 className="font-serif text-2xl">Pošlji povpraševanje</h2>
          {!user && (
            <p className="text-xs text-muted-foreground">
              Za pošiljanje povpraševanja se morate <Link to="/prijava" className="underline">prijaviti</Link>.
            </p>
          )}

          <div className="space-y-3">
            <Field label="Ime in priimek *" value={name} onChange={setName} required maxLength={200} />
            <Field label="Naslov *" value={line1} onChange={setLine1} required maxLength={200} />
            <div className="grid grid-cols-[100px_1fr] gap-2">
              <Field label="Pošta *" value={postal} onChange={setPostal} required maxLength={20} />
              <Field label="Kraj *" value={city} onChange={setCity} required maxLength={100} />
            </div>
            <Field label="Telefon" value={phone} onChange={setPhone} maxLength={50} />
            <label className="block text-xs">
              <span className="text-muted-foreground">Opomba prodajalcu (neobvezno)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </label>
          </div>

          <div className="space-y-2 text-sm pt-2 border-t border-border">
            <div className="flex justify-between"><span className="text-muted-foreground">Vmesna vsota</span><span className="text-accent">€{displayTotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Poštnina</span><span>določi prodajalec</span></div>
            <div className="flex justify-between font-serif text-lg pt-2 border-t border-border">
              <span>Okvirno</span><span className="text-accent">€{displayTotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground">Končni znesek bo na predračunu, ki vam ga pošlje prodajalec.</p>
          </div>

          {err && <p className="text-xs text-destructive">{err}</p>}

          <button
            type="submit"
            disabled={loading || !user}
            className="w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Pošiljam…" : "Pošlji povpraševanje"}
          </button>
          {/* Original total for reference (sanity check vs cart subtotal) */}
          {Math.abs(displayTotal - total) > 0.01 && (
            <p className="text-[10px] text-muted-foreground">Cene v košarici so bile osvežene.</p>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, required, maxLength }: { label: string; value: string; onChange: (v: string) => void; required?: boolean; maxLength?: number }) {
  return (
    <label className="block text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        maxLength={maxLength}
        className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
